import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface ToastContextValue {
  notify: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error('useToast doit être utilisé dans un ToastProvider');
  }
  return ctx;
}

/**
 * Notification brève en bas d'écran (confirmation d'une action réussie) :
 * surface `popover` à filet, rayon lg, ombre shadow-md (réservée aux surfaces
 * flottantes), disque `success` (forme du succès) devant le message.
 */
export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const notify = useCallback((next: string): void => {
    setMessage(next);
    if (timer.current !== null) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => {
      setMessage(null);
    }, 2800);
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {message !== null
        ? createPortal(
            <div
              className="fixed bottom-6 left-1/2 z-[60] flex -translate-x-1/2 animate-toast-in items-center gap-2.5 max-w-[calc(100vw-32px)] rounded-lg border border-border bg-popover px-4 py-2.5 text-small font-medium text-popover-foreground shadow-md"
              role="status"
            >
              <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-success-fg" />
              {message}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}
